import React from "react";

interface KpiStickerProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  /**
   * Logical color key – internally mapped to Droppie brand colors.
   * Existing usages (blue, green, purple, orange, red, indigo, teal, amber)
   * now render using the brand palette (zalm, oranje, lichtblauw, paars, petrol, geel, etc).
   */
  color?:
    | "blue"
    | "green"
    | "purple"
    | "orange"
    | "red"
    | "indigo"
    | "teal"
    | "amber";
}

export const KpiSticker: React.FC<KpiStickerProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = "blue",
}) => {
  /**
   * Map logical color keys to Droppie brand colors (Tailwind extends in tailwind.config.js)
   *
   * Brand palette:
   * - droppie-zalm       (#faebe1)
   * - droppie-lichtroze  (#fae1eb)
   * - droppie-oranje     (#ff6e41)
   * - droppie-lichtblauw (#d2e6ff)
   * - droppie-paars      (#8773aa)
   * - droppie-lichtpaars (#e6e1ff)
   * - droppie-lichtgroen (#d2d2b9)
   * - droppie-petrol     (#007887)
   *
   * Each logical color reuses exactly ONE brand color as its background.
   * We intentionally don't use Geel here (per design feedback).
   */
  const colorClasses = {
    // Zalm
    blue: "bg-droppie-zalm text-droppie-petrol",
    // Oranje
    green: "bg-droppie-oranje text-white",
    // Lichtblauw
    purple: "bg-droppie-lichtblauw text-droppie-petrol",
    // Paars
    orange: "bg-droppie-paars text-white",
    // Lichtpaars
    red: "bg-droppie-lichtpaars text-droppie-petrol",
    // Lichtgroen
    indigo: "bg-droppie-lichtgroen text-droppie-petrol",
    // Petrol
    teal: "bg-droppie-petrol text-white",
    // Lichtroze
    amber: "bg-droppie-lichtroze text-droppie-oranje",
  };

  const iconColorClasses = {
    blue: "text-droppie-petrol",
    green: "text-white",
    purple: "text-droppie-petrol",
    orange: "text-white",
    red: "text-droppie-paars",
    indigo: "text-droppie-petrol",
    teal: "text-white",
    amber: "text-droppie-oranje",
  };

  return (
    <div
      className={`relative p-4 rounded-lg shadow-sm ${colorClasses[color]} transition-all duration-200 hover:shadow-md`}
    >
      {icon && (
        <div
          className={`absolute top-3 right-3 ${iconColorClasses[color]} opacity-70`}
        >
          <div className="w-6 h-6">{icon}</div>
        </div>
      )}
      <div className="flex flex-col">
        <h3 className="text-xs font-medium opacity-75 mb-1 pr-6">{title}</h3>
        <div className="text-md font-bold mb-1 leading-tight">
          {typeof value === "number" ? value.toLocaleString() : value}
        </div>
        {subtitle && (
          <p className="text-xs opacity-60 leading-tight">{subtitle}</p>
        )}
      </div>
    </div>
  );
};
